import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FilterDayDetail from '@/components/filters/FilterDayDetail';
import { notificationService } from '@core/notifications/NotificationService';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';
import { zoneTrackerService } from '@/services/zoneTrackerService.ts';
import ZoneTrackerPageLayout from '@/components/ZoneTrackerPageLayout.tsx';

interface FilterArgs {
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
    interval: number;
}

const ZoneTrackerDailyDetails: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<any[]>([]);

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setLoading(true);
            setSeries([]);

            try {
                const data = await zoneTrackerService.loadDailyDetails(
                    args.deviceId,
                    args.moduleId,
                    args.start,
                    args.end,
                    args.interval
                );

                const newSeries = [
                    { name: 'Max', data: [] as any[] },
                    { name: t('average'), data: [] as any[] },
                    { name: 'Min', data: [] as any[] }
                ];

                data.forEach((element) => {
                    newSeries[0].data.push({ x: element.intervalStart, y: element.max });
                    newSeries[1].data.push({ x: element.intervalStart, y: element.avg });
                    newSeries[2].data.push({ x: element.intervalStart, y: element.min });
                });

                setSeries(newSeries);
            } catch (error) {
                console.error('Failed to load zone tracker daily details:', error);
                notificationService.error(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    return (
        <ZoneTrackerPageLayout
            path="/zonetracker/dailydetails"
            breadcrumbActive="Daily Details"
            titleSlot={`${t('zoneTracker')} ${t('dailyDetails')}`}
            loading={loading}
            cardHeading={t('fillLevel')}
            filterComponent={
                <FilterDayDetail onLoadData={loadData} forType="zoneTracker" intervals={[60, 30, 15, 10, 5, 1]} />
            }
            chartComponent={
                <WcnApexchart
                    type="line"
                    chartId="zoneTrackerDailyDetailsCharts"
                    series={series}
                    height={550}
                    xAxisType="datetime"
                    xAxisShowTime={true}
                />
            }
        />
    );
};

ZoneTrackerDailyDetails.displayName = 'ZoneTrackerDailyDetails';

export default ZoneTrackerDailyDetails;
